<template>
  <div :class="classCompute">
    <component :is="resolvedIcon" v-if="resolvedIcon" class="badge-icon" :width="12" :height="12" :color="iconColor" />
    {{ display }}
  </div>
</template>

<script lang="ts">
import TimeOutlineIcon from '@/graphics/icons/TimeOutlineIcon.vue';

const BADGE_ICONS = {
  'time-outline': TimeOutlineIcon,
} as const;

type BadgeIconName = keyof typeof BADGE_ICONS;

export default {
  name: 'Badge',
  components: {
    TimeOutlineIcon,
  },
  props: {
    variant: {
      type: String,
      default: 'primary',
    },
    badgeContent: {
      type: [Number, String],
      required: true,
    },
    icon: {
      type: String,
      required: false,
      default: null,
      validator(value: string | null) {
        return value == null || value in BADGE_ICONS;
      },
    },
    iconColor: {
      type: String,
      required: false,
      default: 'currentColor',
    },
  },
  computed: {
    resolvedIcon(): (typeof BADGE_ICONS)[BadgeIconName] | null {
      return this.icon ? BADGE_ICONS[this.icon as BadgeIconName] : null;
    },
    display(): String {
      if (typeof this.badgeContent === 'string') return this.badgeContent;
      const numberString = this.badgeContent.toString();
      if (!Number.isInteger(this.badgeContent)) return '';
      if (this.badgeContent < 1) {
        return '0';
      }
      if (this.badgeContent < 100) {
        return numberString;
      }
      return '99+';
    },
    classCompute(): String[] {
      const variantClasses = {
        primary: 'badge-primary',
        subdued: 'badge-subdued',
        strong: 'badge-strong',
        moderate: 'badge-moderate',
        urgent: 'badge-urgent',
      };

      let widthClass = '';
      if (typeof this.badgeContent === 'string' || this.badgeContent > 99) {
        widthClass = 'badge-wide';
      }

      return ['badge', variantClasses[this.variant], widthClass];
    },
  },
};
</script>

<style scoped lang="scss">
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 20px;
  height: 20px;
  text-align: center;
  font-weight: 500;
  border-radius: 50px;
  line-height: 19px;
  font-size: 12px;
  white-space: nowrap;
}

.badge-large {
  width: 28px;
  height: 28px;
  line-height: 27px;
}

.badge-wide {
  width: auto;
  max-width: max-content;
  padding: 0 8px;
}

.badge-primary {
  background-color: theme('colors.primary.DEFAULT');
  color: theme('colors.primary.subdued');
}

.badge-subdued {
  background-color: theme('colors.primary.subdued');
  color: theme('colors.primary.DEFAULT');
}

.badge-strong {
  background-color: #b84d8d;
  color: theme('colors.white');
}

.badge-moderate {
  background-color: #ebccde;
  color: theme('colors.primary.DEFAULT');
}

.badge-urgent {
  background-color: #fff1ec;
  color: #ff5a2a;
  border: 1px solid #ffb49e;
}
</style>
